import { useCallback, useMemo } from "react"
import { useRouter } from "next/router"

import { buildAgentSimulationMetadata, resolveAgent } from "@/agents"
import { useAgentsById } from "@/agents/catalog"
import { normalizeLegacyIterationCount } from "@/constants/iteration-limits"
import { useMetricsList } from "@/metrics/catalog"
import {
  buildMetricIdToNameLookup,
  buildMetricRecordsByName,
  isAutomaticExpectedBehaviorMetric,
} from "@/metrics/registry"
import { useGetOrgRecordTransport } from "@/organization"
import { usePersonasList } from "@/persona"
import { useRunMetadataTransport } from "@/runs"
import { useTemplateById, useTemplatesAll } from "@/templates/catalog"
import { useTestSetsList } from "@/test-set/catalog"

import type { AgentRecord } from "@/types/api/agent"
import type { RunRecord } from "@/types/api/RunRecord"
import type { RunTemplateRecord } from "@/types/api/RunTemplateRecord"
import { SimulatorType } from "@/types/api/SimulatorType"
import {
  asJsonObjectOrNull,
  asJsonObjectValueOrNull,
  type JsonObject,
} from "@/types/json"

import {
  getPrefilledMetricNames,
  getRunMetricSelection,
} from "./selectedMetricIds"
import {
  applyTestCaseIdsOverride,
  parseTestCaseIdsParam,
} from "./testCaseIdsOverride"
import type {
  SimulationLaunchMetricRecordsByName,
  SimulationTemplateData,
} from "./types"

const SIMULATOR_TYPES = new Set<string>(Object.values(SimulatorType))

/**
 * Resolve a model type to the simulator type the launcher submits for it.
 *
 * Runs and agents both carry model types this enum does not name: the backend's MODEL_TYPE_TEXT,
 * and the model-manager class names older runs recorded. Returns null for a type with no launcher
 * equivalent, which leaves the caller on whatever it can infer instead.
 */
function toSimulatorType(modelType: string): SimulatorType | null {
  if (modelType === "MODEL_TYPE_TEXT") {
    return SimulatorType.TEXT
  }
  if (
    modelType === "MODEL_TYPE_LIVEKIT" ||
    modelType === "LiveKitModelManager" ||
    modelType === "manager_livekit" ||
    modelType.toLowerCase().includes("livekit")
  ) {
    return SimulatorType.LIVEKIT
  }
  if (SIMULATOR_TYPES.has(modelType)) {
    return modelType as SimulatorType
  }
  if (
    modelType === "VoiceModelManager" ||
    modelType === "manager_voice" ||
    modelType === "MODEL_TYPE_VOICE"
  ) {
    return SimulatorType.VOICE
  }
  // Inbound voice is MODEL_TYPE_VOICE under the name an older manager used.
  if (
    modelType === "InboundVoiceModelManager" ||
    modelType === "manager_inbound_voice"
  ) {
    return SimulatorType.VOICE
  }
  return null
}

export function mapRunTemplateToTemplateData(
  runTemplate: RunTemplateRecord,
  metricIdToName: Record<string, string>,
  metricRecords: SimulationLaunchMetricRecordsByName = {},
): SimulationTemplateData {
  const enabledMetrics = getPrefilledMetricNames(
    { metricIds: runTemplate.metric_ids || [] },
    metricIdToName,
    metricRecords,
  )

  const primaryAgentId = runTemplate.agent_ids[0] ?? ""
  const mutationIdsByAgent = Object.fromEntries(
    runTemplate.agent_ids.map((agentId) => [agentId, runTemplate.mutation_ids]),
  )

  return {
    templateId: runTemplate.id,
    name: runTemplate.display_name,
    description: runTemplate.description || "",
    agentIds: runTemplate.agent_ids,
    mutationIdsByAgent:
      runTemplate.mutation_ids.length > 0 ? mutationIdsByAgent : {},
    agentId: primaryAgentId,
    testSetIds: runTemplate.test_set_ids,
    personaIds: runTemplate.persona_ids,
    iterationCount: runTemplate.iteration_count,
    enabledMetrics,
    simulationType: SimulatorType.VOICE,
    simulationMetadata: {},
    frequency: "manual",
    concurrency: runTemplate.concurrency,
    subSampleSize: runTemplate.sub_sample_size || 0,
    shouldSubSample: Boolean(runTemplate.sub_sample_size),
    selectedTestCaseIds: runTemplate.test_case_ids,
    firstMessageMode: "assistant-waits-for-user",
    testCaseParamsByIteration: [],
    tags: runTemplate.tags ?? [],
  }
}

export function mapRunConfigToTemplateData(
  runRecord: RunRecord,
  agentRecord?: AgentRecord | null,
  metricIdToName: Record<string, string> = {},
  metricRecords: SimulationLaunchMetricRecordsByName = {},
): SimulationTemplateData {
  const config = runRecord.config

  // Support both old and new config structures
  const modelConfig =
    asJsonObjectOrNull(config?.model_config) ??
    asJsonObjectOrNull(config?.model)
  const modelConfigConfig = asJsonObjectOrNull(modelConfig?.config)
  const modelConfigValues = modelConfigConfig ?? {}

  // Extract simulation metadata (excluding known fields)
  const simulationMetadata: JsonObject = {}
  if (modelConfigConfig) {
    const {
      simulation_prompt,
      agent_id,
      test_case_params_by_iteration,
      api_key,
      persona_id,
      ...metadata
    } = modelConfigConfig
    Object.assign(simulationMetadata, metadata)

    // Parse generate_token_headers if it exists and is a string
    if (
      metadata.generate_token_headers &&
      typeof metadata.generate_token_headers === "string"
    ) {
      try {
        simulationMetadata.generate_token_headers = JSON.parse(
          metadata.generate_token_headers,
        ) as JsonObject
      } catch {
        // Keep the original string value if parsing fails
        simulationMetadata.generate_token_headers =
          metadata.generate_token_headers
      }
    }
  }

  const metricSelection = getRunMetricSelection(config)
  const enabledMetrics = getPrefilledMetricNames(
    metricSelection,
    metricIdToName,
    metricRecords,
  )

  const testCaseParamsByIteration =
    (modelConfigValues.test_case_params_by_iteration as JsonObject[]) || []

  const personaIds = (modelConfigValues.persona_id as string | undefined)
    ? [modelConfigValues.persona_id as string]
    : []

  // Determine the correct simulator type by checking the model type and manager
  let simulationType: SimulatorType = SimulatorType.VOICE // Default fallback

  const modelType = modelConfig?.type as string | undefined

  if (modelType) {
    const storedSimulationType = toSimulatorType(modelType)
    if (storedSimulationType) {
      simulationType = storedSimulationType
    } else if (simulationMetadata.phone_number || simulationMetadata.voice_id) {
      simulationType = SimulatorType.VOICE
    }
  } else if (
    // If no model type, try to infer from LiveKit-specific fields in metadata
    simulationMetadata.generate_token_headers ||
    simulationMetadata.generate_token_endpoint ||
    simulationMetadata.livekit_url ||
    simulationMetadata.livekit_agent_name
  ) {
    simulationType = SimulatorType.LIVEKIT
  }

  // A metadata blob only means something alongside the model type it was
  // written for, so the agent supplies both or neither. When it does, its
  // current config replaces the run's copy outright: a value the agent changed
  // is updated, and one it no longer sets is dropped. A run whose agent is
  // gone, or whose agent reports a model type with no launcher equivalent,
  // keeps the stored config, which is all that is left of the config that ran.
  const agentSimulationType = agentRecord
    ? toSimulatorType(agentRecord.model_type)
    : null
  const agentOwnsConfig = agentRecord != null && agentSimulationType != null

  if (agentSimulationType) {
    simulationType = agentSimulationType
  }

  return {
    templateId: "no-template",
    name: `${runRecord.display_name} - Template`,
    description: `Template created from run ${runRecord.display_name}`,
    agentId: runRecord.agent_id || "",
    agentIds: runRecord.agent_id ? [runRecord.agent_id] : [],
    testSetIds: runRecord.test_set_id ? [runRecord.test_set_id] : [],
    iterationCount: normalizeLegacyIterationCount(config?.iteration_count),
    enabledMetrics,
    simulationType,
    simulationMetadata: agentOwnsConfig
      ? buildAgentSimulationMetadata(agentRecord)
      : simulationMetadata,
    frequency: "manual", // Default frequency for templates created from runs
    concurrency: Number(config?.concurrency) || 1,
    subSampleSize: Number(config?.sub_sample_size) || 0,
    shouldSubSample: Boolean(Number(config?.sub_sample_size) || 0),
    selectedTestCaseIds:
      (config?.test_case_ids as string[] | undefined) || undefined,
    personaIds,
    firstMessageMode: "assistant-waits-for-user",
    testCaseParamsByIteration,
    customerMetadata:
      asJsonObjectValueOrNull(runRecord.customer_metadata) ?? undefined,
    sourceRunId: runRecord.id,
    hasAutomaticMetrics: metricSelection.hasAutomaticMetrics,
  }
}

export function useEvaluationData(
  _organizationId: string,
  selectedTemplateId = "",
) {
  const router = useRouter()

  const runId = router.query.run_id as string
  const hasRunId = !!runId
  const templateId =
    typeof router.query.template_id === "string" ? router.query.template_id : ""
  const hasTemplateId = !!templateId
  const testCaseIdsParam = router.query.test_case_ids
  const testCaseIdsOverride = useMemo(
    () => parseTestCaseIdsParam(testCaseIdsParam),
    [testCaseIdsParam],
  )

  const testSetsQuery = useTestSetsList({ select: (data) => data || [] })

  const metricsQuery = useMetricsList()

  const templatesQuery = useTemplatesAll()

  const templateQuery = useTemplateById(templateId, {
    enabled: hasTemplateId && !hasRunId,
    refetchOnMount: "always",
  })

  const organizationQuery = useGetOrgRecordTransport()

  const personasQuery = usePersonasList({ select: (data) => data || [] })

  const runQuery = useRunMetadataTransport(runId, { enabled: hasRunId })

  // Inactive agents are included, so a run whose agent was archived still
  // prefills from that agent's current config.
  const {
    agentsById,
    isFetchedAfterMount: agentsFetched,
    isError: agentsError,
    refetch: refetchAgents,
  } = useAgentsById({
    // The lookup profile holds agents for five minutes and does not revalidate
    // on mount, so without this the prefill reads the agent as of the last
    // local invalidation and an edit made elsewhere is silently missed. Waiting
    // on isFetchedAfterMount rather than isLoading is what makes the override
    // count: the launcher takes the first template data it sees, so a
    // background revalidation would land after the prefill was already built.
    //
    // The query mounts unconditionally because refetchOnMount only applies on
    // the mount path: an observer that mounts disabled and is enabled later
    // refetches on staleness alone, which would leave isFetchedAfterMount
    // false against a fresh cache and the page waiting on it forever.
    refetchOnMount: "always",
  })

  // Only a run that names an agent has a prefill to wait on.
  const runAgentId = runQuery.data?.run?.agent_id

  const runAgent = useMemo(
    () => (runAgentId ? resolveAgent(runAgentId, agentsById) : null),
    [agentsById, runAgentId],
  )

  // A fetch that errored settles isFetchedAfterMount and leaves whatever the
  // cache already held in place, so the lookup can arrive holding other agents
  // and not this one. Prefilling from the run's stored copy there is the
  // staleness this path exists to avoid, so the page reports the failure
  // instead. A lookup that resolved this agent is enough, whether or not the
  // fetch that carried it was the one that failed.
  const runAgentUnresolved = !!runAgentId && !runAgent && !!agentsError

  const referencedMetricIds = useMemo(() => {
    const runSelection = getRunMetricSelection(runQuery.data?.run?.config)
    const ids = selectedTemplateId
      ? templatesQuery.data?.templates?.find(
          (template) => template.id === selectedTemplateId,
        )?.metric_ids
      : hasRunId
        ? "metricIds" in runSelection
          ? runSelection.metricIds
          : runSelection.legacyKeys
        : templateQuery.data?.metric_ids
    return [...new Set(ids ?? [])]
  }, [
    selectedTemplateId,
    hasRunId,
    runQuery.data?.run?.config,
    templateQuery.data?.metric_ids,
    templatesQuery.data?.templates,
  ])
  const prefillMetricsQuery = useMetricsList({
    referencedMetricIds,
    revalidateOnMount: true,
  })

  const metricIdToName = useMemo(
    () =>
      buildMetricIdToNameLookup(
        prefillMetricsQuery.data,
        prefillMetricsQuery.metricIdAliases,
      ),
    [prefillMetricsQuery.data, prefillMetricsQuery.metricIdAliases],
  )

  const metricRecords = useMemo(
    () => buildMetricRecordsByName(prefillMetricsQuery.data),
    [prefillMetricsQuery.data],
  )

  const selectableMetricRecords = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(metricRecords).filter(
          ([, metric]) => !isAutomaticExpectedBehaviorMetric(metric),
        ),
      ),
    [metricRecords],
  )

  const templateRecords = useMemo(() => {
    if (prefillMetricsQuery.isLoading) {
      return []
    }
    const rawTemplates = templatesQuery.data?.templates || []
    return rawTemplates.map((template) =>
      mapRunTemplateToTemplateData(template, metricIdToName, metricRecords),
    )
  }, [
    templatesQuery.data?.templates,
    metricIdToName,
    metricRecords,
    prefillMetricsQuery.isLoading,
  ])

  const initialTemplateData = useMemo(() => {
    if (
      !prefillMetricsQuery.data ||
      prefillMetricsQuery.isLoading ||
      prefillMetricsQuery.error
    ) {
      return null
    }
    if (!hasRunId) {
      return hasTemplateId && templateQuery.data
        ? mapRunTemplateToTemplateData(
            templateQuery.data,
            metricIdToName,
            metricRecords,
          )
        : null
    }

    const run = runQuery.data?.run

    // Withheld until the agent resolves, because the launcher initializes from
    // the first template data it sees and never re-reads it for the same run.
    if (!run || (run.agent_id && !agentsFetched) || runAgentUnresolved) {
      return null
    }

    return applyTestCaseIdsOverride(
      mapRunConfigToTemplateData(run, runAgent, metricIdToName, metricRecords),
      testCaseIdsOverride,
    )
  }, [
    agentsFetched,
    hasRunId,
    hasTemplateId,
    metricIdToName,
    metricRecords,
    prefillMetricsQuery.data,
    prefillMetricsQuery.isLoading,
    prefillMetricsQuery.error,
    runAgent,
    runAgentUnresolved,
    runQuery.data?.run,
    templateQuery.data,
    testCaseIdsOverride,
  ])

  const isLoading =
    testSetsQuery.isLoading ||
    metricsQuery.isLoading ||
    prefillMetricsQuery.isLoading ||
    templatesQuery.isLoading ||
    organizationQuery.isLoading ||
    personasQuery.isLoading ||
    (hasTemplateId && !hasRunId && !templateQuery.isFetchedAfterMount) ||
    (hasRunId && runQuery.isLoading) ||
    (!!runAgentId && !agentsFetched)

  const hasError =
    testSetsQuery.error ||
    metricsQuery.error ||
    prefillMetricsQuery.error ||
    templatesQuery.error ||
    organizationQuery.error ||
    personasQuery.error ||
    (hasTemplateId && !hasRunId && templateQuery.error) ||
    (hasRunId && runQuery.error) ||
    runAgentUnresolved

  const refetchData = useCallback(
    () =>
      Promise.all([
        testSetsQuery.refetch(),
        metricsQuery.refetch(),
        prefillMetricsQuery.refetch(),
        templatesQuery.refetch(),
        organizationQuery.refetch(),
        personasQuery.refetch(),
        ...(hasRunId ? [runQuery.refetch(), refetchAgents()] : []),
        ...(hasTemplateId && !hasRunId ? [templateQuery.refetch()] : []),
      ]),
    [
      testSetsQuery,
      metricsQuery,
      prefillMetricsQuery,
      templatesQuery,
      organizationQuery,
      personasQuery,
      hasRunId,
      runQuery,
      refetchAgents,
      hasTemplateId,
      templateQuery,
    ],
  )

  return {
    refetchData,
    testSetRecords: testSetsQuery.data || [],
    metricRecords: selectableMetricRecords,
    templateRecords,
    organizationRecord: organizationQuery.data,
    personaRecords: personasQuery.data || [],
    initialTemplateData,

    hasRunId,
    hasTemplateId,
    isLoading,
    hasError,
    // Expose refetch methods if needed for domain-controlled data refresh
    refetchTemplates: templatesQuery.refetch,
    refetchTestSets: testSetsQuery.refetch,
    refetchMetrics: prefillMetricsQuery.refetch,
    refetchOrganization: organizationQuery.refetch,
    refetchPersonas: personasQuery.refetch,
  }
}
